import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Sort } from '@angular/material/sort'
import { Maksukortti } from '@lemon/core-firebase/model/maksukortti'
import { WebMaksukorttiPoistaPyynto, WebMaksukorttiPoistaVastaus } from '@lemon/core-firebase/model/web-maksukortti-api'
import { Maksutapa } from '@lemon/core-firebase/model/tosite'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { catchError, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { TositeService } from '../../_angular/service/tosite/tosite.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../_jaettu-angular/_components/are-you-sure.dialog'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { MaksukorttiLisaaDialog, MaksukorttiLisaaDialogData } from './maksukortti-lisaa.dialog'
import { muotoileKortinNumero } from './maksukortti-logiikka'
import { MaksukorttiService } from './maksukortti.service'

export interface NaytettavaMaksukortti {
  avain: string
  viimeiset4: string
  kortinNumero: string
  nimi: string
  maksutapaNimi: string
  maksutapaJarjestys: number
}

interface MaksukorttilistanTila {
  kortit: NaytettavaMaksukortti[]
  virhe: boolean
  lataa: boolean
}

@Component({
  templateUrl: './maksukortit.component.html',
  styleUrls: ['./maksukortit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksukortitComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _yritaUudelleenSubject = new BehaviorSubject<void>(undefined)
  private _jarjestysSubject = new BehaviorSubject<Sort | null>(null)
  private _kortit: Maksukortti[] = []
  private _maksutavat: Maksutapa[] = []

  propertiesToDisplay = ['kortinNumero', 'nimi', 'maksutapa', 'toiminnot']
  tilaObservable: Observable<MaksukorttilistanTila>

  constructor(
    private _maksukorttiService: MaksukorttiService,
    private _tositeService: TositeService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    const kortitTaiVirheObservable = this._yritaUudelleenSubject.pipe(
      switchMap(() =>
        this._maksukorttiService.annaMaksukortitObservable().pipe(
          map(kortit => ({ kortit, virhe: false, lataa: false })),
          startWith({ kortit: [] as Maksukortti[], virhe: false, lataa: true }),
          catchError(err => {
            console.error('Maksukorttien haku epäonnistui', err)
            return of({ kortit: [] as Maksukortti[], virhe: true, lataa: false })
          }),
        ),
      ),
    )

    this.tilaObservable = combineLatest([kortitTaiVirheObservable, this._tositeService.maksutavatObservable, this._jarjestysSubject]).pipe(
      tap(([tila, maksutavat]) => {
        this._kortit = tila.kortit
        this._maksutavat = maksutavat
      }),
      map(([tila, maksutavat, jarjestys]) => ({
        kortit: this._jarjesta(this._muunnaNaytettaviksi(tila.kortit, maksutavat), jarjestys),
        virhe: tila.virhe,
        lataa: tila.lataa,
      })),
      takeUntil(this._ngUnsubscribe),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  yritaUudelleen() {
    this._yritaUudelleenSubject.next()
  }

  jarjesta(jarjestys: Sort) {
    this._jarjestysSubject.next(jarjestys)
  }

  lisaa() {
    const data: MaksukorttiLisaaDialogData = { olemassaolevat: this._kortit }
    this._dialog.open(MaksukorttiLisaaDialog, { data })
  }

  async poista(kortti: NaytettavaMaksukortti) {
    const data: AreYouSureDialogData = {
      header: this._translationService.lokalisoi('tositteet.maksukortit.poista-otsikko'),
      text: this._translationService.lokalisoi('tositteet.maksukortit.poista-teksti', { numero: kortti.kortinNumero }),
      rightAction: this._translationService.lokalisoi('yleiset.poista'),
    }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }
    try {
      const pyynto: WebMaksukorttiPoistaPyynto = { avain: kortti.avain }
      const vastaus = await this._firebaseLemonaid.functionsCall<WebMaksukorttiPoistaPyynto, WebMaksukorttiPoistaVastaus>('maksukorttiPoistaWeb', pyynto, undefined, 'europe-north1')
      if (vastaus.e) {
        throw new Error(`Maksukortin ${kortti.avain} poisto epäonnistui: ${vastaus.e}`)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open(this._translationService.lokalisoi('tositteet.maksukortit.poisto-epaonnistui'), 'OK', { duration: 10000 })
    }
  }

  private _muunnaNaytettaviksi(kortit: Maksukortti[], maksutavat: Maksutapa[]): NaytettavaMaksukortti[] {
    return kortit.map(kortti => {
      const maksutapa = maksutavat.find(m => m.i === kortti.maksutapaTunniste)
      return {
        avain: kortti.avain,
        viimeiset4: kortti.viimeiset4,
        kortinNumero: muotoileKortinNumero(kortti.viimeiset4),
        nimi: kortti.nimi ?? '',
        maksutapaNimi: maksutapa?.l ?? '',
        maksutapaJarjestys: maksutapa?.o ?? Number.MAX_SAFE_INTEGER,
      }
    })
  }

  private _jarjesta(kortit: NaytettavaMaksukortti[], jarjestys: Sort | null): NaytettavaMaksukortti[] {
    const oletus = (a: NaytettavaMaksukortti, b: NaytettavaMaksukortti) => a.maksutapaJarjestys - b.maksutapaJarjestys || a.viimeiset4.localeCompare(b.viimeiset4)
    if (!jarjestys?.direction) {
      return [...kortit].sort(oletus)
    }
    const suunta = jarjestys.direction === 'asc' ? 1 : -1
    const vertaa = (a: NaytettavaMaksukortti, b: NaytettavaMaksukortti) => {
      switch (jarjestys.active) {
        case 'nimi':
          return a.nimi.localeCompare(b.nimi)
        case 'maksutapa':
          return a.maksutapaJarjestys - b.maksutapaJarjestys
        default:
          return a.viimeiset4.localeCompare(b.viimeiset4)
      }
    }
    return [...kortit].sort((a, b) => suunta * vertaa(a, b) || oletus(a, b))
  }
}
